import { useMemo } from 'react'
import { Box, Button, FormControl, MenuItem, Paper, Select, Stack, TextField, Typography } from '@mui/material'
import { useOnboarding } from '../../context/OnboardingContext'
import { MISC_HANDLED_STATES } from '../../data/miscHandledStates'
import { licenseDisplayName, type LicenseRow } from '../../types/onboarding'
import { SelectedStatesPicker } from '../components/SelectedStatesPicker'
import { companyNameOptions } from '../licenseSteps/businessEntityNames'
import { LicenseAddMoreButton } from '../licenseSteps/LicenseAddMoreButton'
import { createEmptyDrlpIndividual } from '../licenseSteps/licenseRowFactory'
import { MISC_PRIMARY, MISC_PRIMARY_HOVER } from '../../theme/onboardingTheme'

const fieldSx = {
  mt: 1,
  width: '100%',
  '& .MuiOutlinedInput-root': {
    fontSize: 14,
    '& fieldset': { borderColor: '#cbd5e1' },
    '&:hover fieldset': { borderColor: MISC_PRIMARY },
    '&.Mui-focused fieldset': { borderColor: MISC_PRIMARY },
  },
  '& .MuiOutlinedInput-input::placeholder': { color: '#94a3b8', opacity: 1 },
}

export function StepDrlpAssignment() {
  const { state, dispatch, goToStep } = useOnboarding()

  const handledStateOptions = useMemo(
    () => MISC_HANDLED_STATES.filter((s) => state.miscHandledStates.includes(s.code)),
    [state.miscHandledStates],
  )

  const drlpIndividuals = useMemo(
    () => state.licenses.filter((l) => l.entity === 'Individual' && l.isDrlp),
    [state.licenses],
  )

  const entityNameOptions = useMemo(
    () =>
      companyNameOptions(
        state.licenses,
        drlpIndividuals.map((r) => r.companyName),
      ),
    [state.licenses, drlpIndividuals],
  )

  const patchLicense = (id: string, patch: Partial<LicenseRow>) => {
    dispatch({ type: 'UPDATE_LICENSE', id, patch })
  }

  return (
    <Stack spacing={4}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff',
          p: { xs: 3, sm: 4 },
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
        }}
      >
        <Typography
          component="h3"
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 700, color: '#0f172a' }}
        >
          DRLP (Designated Responsible Licensed Producer)
        </Typography>
        <Typography sx={{ mt: 1.5, fontSize: 16, lineHeight: 1.625, color: '#1e293b' }}>
          Review the individuals marked as DRLP on the Individual Licenses step, or add another
          designated producer below. For each person, select the state(s) from your General Questions
          selection where they serve as the Designated Responsible Licensed Producer.
        </Typography>
      </Paper>

      {drlpIndividuals.length === 0 ? (
        <Box
          sx={{
            borderRadius: '12px',
            border: '1px dashed #cbd5e1',
            bgcolor: '#f8fafc',
            px: 3,
            py: 4,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: 14, color: '#334155' }}>
            No DRLPs added yet. Use <Typography component="span" sx={{ fontWeight: 600, fontSize: 'inherit' }}>Add More</Typography> below, or return
            to{' '}
            <Button
              type="button"
              onClick={() => goToStep('individualLicenses')}
              sx={{
                minWidth: 0,
                p: 0,
                verticalAlign: 'baseline',
                fontWeight: 600,
                color: MISC_PRIMARY,
                textDecoration: 'underline',
                '&:hover': {
                  color: MISC_PRIMARY_HOVER,
                  bgcolor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              Individual Licenses
            </Button>{' '}
            and check the DRLP? box for an existing licensee.
          </Typography>
        </Box>
      ) : (
        drlpIndividuals.map((person, index) => {
          const assigned = state.drlpStateAssignments[person.licenseeId] ?? []
          const idPrefix = `drlp-${person.licenseeId}`
          const displayName = licenseDisplayName(person)
          const nameIsEditable = person.source === 'Manual'

          return (
            <Paper
              key={person.licenseeId}
              elevation={0}
              sx={{
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                p: { xs: 3, sm: 4 },
                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
              }}
            >
              <Box sx={{ borderBottom: '1px solid #f1f5f9', pb: 2 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: '#64748b',
                  }}
                >
                  DRLP {index + 1} of {drlpIndividuals.length}
                </Typography>
                {nameIsEditable ? (
                  <Box sx={{ mt: 1.5, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                    <Box>
                      <Typography component="label" sx={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#334155' }}>
                        First Name
                      </Typography>
                      <TextField
                        size="small"
                        sx={fieldSx}
                        value={person.licenseeFirstName}
                        onChange={(e) =>
                          patchLicense(person.licenseeId, {
                            licenseeFirstName: e.target.value,
                          })
                        }
                        placeholder="e.g. Jane"
                        inputProps={{ 'aria-label': 'DRLP First Name' }}
                      />
                    </Box>
                    <Box>
                      <Typography component="label" sx={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#334155' }}>
                        Last Name
                      </Typography>
                      <TextField
                        size="small"
                        sx={fieldSx}
                        value={person.licenseeLastName}
                        onChange={(e) =>
                          patchLicense(person.licenseeId, {
                            licenseeLastName: e.target.value,
                          })
                        }
                        placeholder="e.g. Smith"
                        inputProps={{ 'aria-label': 'DRLP Last Name' }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Typography component="h4" sx={{ mt: 0.5, fontSize: '1.125rem', fontWeight: 600, color: '#0f172a' }}>
                    {displayName}
                  </Typography>
                )}
                <Box sx={{ mt: 1.5, display: 'block', maxWidth: 448 }}>
                  <Typography component="label" sx={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#334155' }}>
                    Company
                  </Typography>
                  <FormControl size="small" fullWidth sx={fieldSx}>
                    <Select
                      value={person.companyName ?? ''}
                      onChange={(e) =>
                        patchLicense(person.licenseeId, { companyName: e.target.value })
                      }
                      inputProps={{ 'aria-label': 'DRLP company' }}
                      disabled={entityNameOptions.length === 0}
                      displayEmpty
                    >
                      <MenuItem value="">
                        {entityNameOptions.length === 0
                          ? 'Add entities on Business Licenses first'
                          : 'Select entity…'}
                      </MenuItem>
                      {entityNameOptions.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ mt: 1.5, display: 'block', maxWidth: 320 }}>
                  <Typography component="label" sx={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#334155' }}>
                    NPN
                  </Typography>
                  <TextField
                    size="small"
                    sx={{
                      ...fieldSx,
                      '& .MuiInputBase-input': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' },
                    }}
                    value={person.npn}
                    onChange={(e) => patchLicense(person.licenseeId, { npn: e.target.value })}
                    inputProps={{ 'aria-label': 'DRLP NPN' }}
                  />
                </Box>
              </Box>

              <Box sx={{ mt: 3 }}>
                <SelectedStatesPicker
                  title="Which state(s) is this individual the DRLP for?"
                  states={handledStateOptions}
                  selectedCodes={assigned}
                  idPrefix={idPrefix}
                  onToggle={(code) =>
                    dispatch({
                      type: 'TOGGLE_DRLP_STATE',
                      licenseeId: person.licenseeId,
                      code,
                    })
                  }
                  onSelectAll={() =>
                    dispatch({
                      type: 'SET_DRLP_STATES',
                      licenseeId: person.licenseeId,
                      codes: handledStateOptions.map((s) => s.code),
                    })
                  }
                  onClearAll={() =>
                    dispatch({
                      type: 'SET_DRLP_STATES',
                      licenseeId: person.licenseeId,
                      codes: [],
                    })
                  }
                />
              </Box>
            </Paper>
          )
        })
      )}

      <LicenseAddMoreButton createRow={createEmptyDrlpIndividual} />
    </Stack>
  )
}
